import React, { useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import AWS from 'aws-sdk';
import Layout from '../components/Layout';
import '../styles/computer-vision.css';
import '../styles/service-fix.css'; // Fix for dark text on dark background

const ComputerVision = ({ user }) => {
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    useCase: '',
    visionType: 'detection'
  });
  const [submitStatus, setSubmitStatus] = useState('');
  
  const isAuthenticated = user !== null;

  if (!isAuthenticated) {
    return (
      <Layout>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Authentication Required</h2>
          <p>Please sign in to access computer vision services.</p>
        </div>
      </Layout>
    );
  }

  const handleContactInfoChange = (field, value) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactInfo.name || !contactInfo.email || !contactInfo.useCase) {
      setSubmitStatus('Please provide your name, email address, and use case.');
      return;
    }
    
    setSubmitStatus('Submitting...');
    
    try {
      // Get AWS credentials from Amplify Auth
      const session = await fetchAuthSession();
      const credentials = session.credentials;
      
      // Configure AWS SDK with proper credentials
      AWS.config.update({
        region: 'us-east-1',
        credentials: {
          accessKeyId: credentials.accessKeyId,
          secretAccessKey: credentials.secretAccessKey,
          sessionToken: credentials.sessionToken
        }
      });
      
      // Send email notification
      const ses = new AWS.SES();
      const emailParams = {
        Source: 'info@relufox.ai',
        Destination: {
          ToAddresses: ['info@relufox.ai']
        },
        Message: {
          Subject: {
            Data: 'New Computer Vision Inquiry - Fox River AI'
          },
          Body: {
            Text: {
              Data: `A new inquiry has been submitted for computer vision services:\n\nCONTACT INFORMATION:\nName: ${contactInfo.name}\nEmail: ${contactInfo.email}\nCompany: ${contactInfo.company || 'Not provided'}\nPhone: ${contactInfo.phone || 'Not provided'}\n\nVISION TYPE: ${contactInfo.visionType}\n\nUSE CASE:\n${contactInfo.useCase}\n\nPlease follow up with this customer.`
            }
          }
        }
      };
      
      await ses.sendEmail(emailParams).promise();
      
      setSubmitStatus('Thank you! Your inquiry has been submitted. We will contact you within 24 hours.');
      setContactInfo({
        name: '',
        email: '',
        company: '',
        phone: '',
        useCase: '',
        visionType: 'detection'
      });
      
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus(`Submission failed: ${error.message || 'Unknown error'}. Please try again or contact us directly.`);
    }
  };

  return (
    <Layout>
      <div className="computer-vision-page">
        <div className="hero-section">
          <h1>Computer Vision</h1>
          <p className="hero-subtitle">Turn visual data into actionable insights with AI-powered image and video analysis</p>
        </div>

        <div className="content-container">
          <section className="service-overview">
            <h2>Unlock Value from Visual Data</h2>
            <p className="section-intro">We help businesses unlock value from visual data using AI-powered image and video analysis. Our computer vision solutions automate tasks like defect detection, inventory tracking, object counting, and visual quality control — improving speed, accuracy, and consistency across your operations.</p>
            
            <div className="services-grid">
              <div className="service-item">
                <h3>Object Detection</h3>
                <p>Identify and locate objects within images and video streams</p>
              </div>
              <div className="service-item">
                <h3>Image Classification</h3>
                <p>Automatically categorize images based on their content</p>
              </div>
              <div className="service-item">
                <h3>Optical Character Recognition</h3>
                <p>Extract text from images, documents, and handwritten forms</p>
              </div>
              <div className="service-item">
                <h3>Image Segmentation</h3>
                <p>Precisely identify boundaries of objects within images</p>
              </div>
            </div>
          </section>

          <section className="applications-section">
            <h2>Industry Applications</h2>
            <div className="applications-grid">
              <div className="application-item">
                <h3>Manufacturing</h3>
                <p>Defect detection, quality control, and assembly line monitoring</p>
              </div>
              <div className="application-item">
                <h3>Retail</h3>
                <p>Inventory tracking, shelf monitoring, and customer behavior analysis</p>
              </div>
              <div className="application-item">
                <h3>Logistics</h3>
                <p>Package scanning, sorting automation, and damage detection</p>
              </div>
              <div className="application-item">
                <h3>Healthcare</h3>
                <p>Medical image analysis, patient monitoring, and diagnostic assistance</p>
              </div>
            </div>
          </section>

          <section className="technology-section">
            <h2>Our Technology</h2>
            <div className="tech-features">
              <div className="tech-item">
                <h4>🔍 Deep Learning Models</h4>
                <p>State-of-the-art neural networks trained for visual recognition tasks</p>
              </div>
              <div className="tech-item">
                <h4>⚡ Real-time Processing</h4>
                <p>Efficient algorithms for processing video streams with minimal latency</p>
              </div>
              <div className="tech-item">
                <h4>📱 Edge Deployment</h4>
                <p>Models optimized to run on local devices without cloud connectivity</p>
              </div>
              <div className="tech-item">
                <h4>🔄 Continuous Learning</h4>
                <p>Systems that improve over time with new data and feedback</p>
              </div>
            </div>
          </section>

          <section className="inquiry-section">
            <h2>Transform Your Visual Data</h2>
            <p>Tell us about your business challenges, and we'll explore how computer vision can help automate and improve your operations.</p>
            
            <form className="inquiry-form" onSubmit={handleSubmit}>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Full Name *" 
                  value={contactInfo.name}
                  onChange={(e) => handleContactInfoChange('name', e.target.value)}
                  className="form-input"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Email Address *" 
                  value={contactInfo.email}
                  onChange={(e) => handleContactInfoChange('email', e.target.value)}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <input 
                  type="text" 
                  placeholder="Company (Optional)" 
                  value={contactInfo.company}
                  onChange={(e) => handleContactInfoChange('company', e.target.value)}
                  className="form-input"
                />
                <input 
                  type="tel" 
                  placeholder="Phone (Optional)" 
                  value={contactInfo.phone}
                  onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                  className="form-input"
                />
              </div>
              <div className="form-row">
                <select
                  value={contactInfo.visionType}
                  onChange={(e) => handleContactInfoChange('visionType', e.target.value)}
                  className="form-select"
                >
                  <option value="detection">Object Detection</option>
                  <option value="classification">Image Classification</option>
                  <option value="ocr">Optical Character Recognition (OCR)</option>
                  <option value="segmentation">Image Segmentation</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <textarea
                placeholder="Describe your business challenge or computer vision needs *"
                value={contactInfo.useCase}
                onChange={(e) => handleContactInfoChange('useCase', e.target.value)}
                className="form-textarea"
                rows="5"
                required
              ></textarea>
              <button type="submit" className="submit-btn">
                Request Consultation
              </button>
              {submitStatus && <p className="submit-status">{submitStatus}</p>}
            </form>
            
            <div className="contact-alternative">
              <p>Prefer to discuss your needs directly?</p>
              <a href="tel:6309408837" className="contact-btn">Call (630) 940-8837</a>
              <p>or email us at <a href="mailto:info@relufox.ai">info@relufox.ai</a></p>
            </div>
          </section>

          <section className="process-section">
            <h2>Our Development Process</h2>
            <div className="process-steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3>Data Assessment</h3>
                <p>Evaluating your visual data and defining project requirements</p>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <h3>Model Development</h3>
                <p>Building and training computer vision models for your specific needs</p>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <h3>Integration</h3>
                <p>Implementing the solution into your existing systems and workflows</p>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <h3>Deployment & Optimization</h3>
                <p>Launching the solution and continuously improving performance</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default ComputerVision;